import React, { useEffect, useState } from "react";
import { Upload, Loader2, Trash2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_COUNT = 6;
const FROZEN_STATUSES = ["in_progress", "awaiting_approval", "completed", "disputed", "refunded"];

/**
 * Client-uploaded reference photos for a task — e.g. a photo of the family
 * member to check on, or the exact item to buy. Visible to client, assigned
 * agent and admin. Editable by the client until the task leaves `assigned`.
 */
export default function ReferencePhotos({ task, role, isClient, wsSignal = 0 }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);

  const frozen = FROZEN_STATUSES.includes(task?.status);
  const canEdit = isClient && !frozen;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/tasks/${task.task_id}/reference-photos`);
        if (mounted) setPhotos(data);
      } catch (err) {
        if (mounted) toast.error(formatApiError(err));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [task.task_id, wsSignal]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      toast.error("Only JPG, PNG or WEBP images allowed");
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Image is too large (max 5 MB)");
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result || "");
    reader.readAsDataURL(f);
  };

  const upload = async () => {
    if (!file) {
      toast.error("Pick a photo first");
      return;
    }
    if (photos.length >= MAX_COUNT) {
      toast.error(`Max ${MAX_COUNT} reference photos per task`);
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: up } = await api.post("/uploads", form);
      const { data: ref } = await api.post(
        `/tasks/${task.task_id}/reference-photos`,
        { image_url: up.url, caption: caption.trim() || null }
      );
      setPhotos((prev) => [...prev, ref]);
      setFile(null);
      setPreview("");
      setCaption("");
      toast.success("Reference photo added — your agent can see it now");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (refId) => {
    if (!window.confirm("Delete this reference photo?")) return;
    try {
      await api.delete(`/tasks/${task.task_id}/reference-photos/${refId}`);
      setPhotos((prev) => prev.filter((p) => p.ref_id !== refId));
      toast.success("Removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Hide entirely when there's nothing to show and the viewer can't add any
  if (!loading && photos.length === 0 && !canEdit) return null;

  return (
    <div className="eb-card p-6 md:p-8" data-testid="reference-photos-card">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-2xl">Reference photos</h2>
          <p className="text-xs text-[var(--eb-text-dim)] mt-1">
            {isClient
              ? "Share who or what your agent should look for — a family member, the exact item, or the address."
              : `${task.client_name || "The client"} added these for context. Treat them as the brief.`}
          </p>
        </div>
        <span className="text-xs text-[var(--eb-text-dim)]" data-testid="reference-photo-count">
          {photos.length}/{MAX_COUNT}
        </span>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--eb-text-dim)]">Loading…</div>
      ) : photos.length === 0 ? (
        <div className="text-sm text-[var(--eb-text-dim)]" data-testid="reference-photos-empty">
          No reference photos yet.
        </div>
      ) : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="reference-photos-list">
          {photos.map((p) => (
            <li key={p.ref_id} className="relative group" data-testid="reference-photo-item" data-ref-id={p.ref_id}>
              <a
                href={`${BACKEND}${p.image_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <img
                  src={`${BACKEND}${p.image_url}`}
                  alt={p.caption || "reference"}
                  className="w-full h-32 object-cover rounded-md border border-[var(--eb-border)]"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              </a>
              {p.caption && (
                <div className="mt-1 text-[11px] text-[var(--eb-text-dim)] line-clamp-2" title={p.caption}>
                  {p.caption}
                </div>
              )}
              {canEdit && (
                <button
                  data-testid="reference-photo-delete"
                  onClick={() => remove(p.ref_id)}
                  className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-white/95 text-[var(--eb-danger)] border border-[var(--eb-border)] opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && photos.length < MAX_COUNT && (
        <div className="mt-6 border-t border-[var(--eb-border)] pt-6">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">
            Add a reference photo
          </div>
          <div className="mt-3 grid sm:grid-cols-3 gap-3">
            <label className="eb-elev p-3 flex flex-col items-center justify-center text-center cursor-pointer hover:border-[var(--eb-primary)] min-h-[120px]">
              {preview ? (
                <img src={preview} alt="" className="w-full h-24 object-cover rounded" />
              ) : (
                <>
                  <Upload className="w-5 h-5 text-[var(--eb-text-dim)]" />
                  <span className="text-xs mt-2 text-[var(--eb-text-dim)]">Click to pick a photo</span>
                  <span className="text-[10px] mt-1 text-[var(--eb-text-faint)]">JPG · PNG · WEBP · max 5 MB</span>
                </>
              )}
              <input
                data-testid="reference-photo-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={onFile}
                className="hidden"
                disabled={busy}
              />
            </label>
            <input
              data-testid="reference-photo-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="eb-input sm:col-span-2"
              maxLength={240}
              placeholder="Caption (optional) — e.g. 'iPhone 15 Pro Max, Natural Titanium'"
            />
          </div>
          <div className="mt-3 flex justify-end">
            <button
              data-testid="reference-photo-submit"
              onClick={upload}
              disabled={busy || !file}
              className="eb-btn eb-btn-primary !py-2 !px-4 !text-sm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
              {busy ? "Uploading…" : "Add photo"}
            </button>
          </div>
        </div>
      )}

      {isClient && frozen && (
        <div className="mt-5 text-[11px] text-[var(--eb-text-faint)]" data-testid="reference-photos-frozen">
          Reference photos are locked now that the agent has started — they preserve the original brief.
        </div>
      )}
    </div>
  );
}
